<script lang="ts" setup>
  import type { PropType } from 'vue';
  import { useHttpClient, useTranslator } from '@othercommerce/foundation';
  import type { Pagination } from '@storefront/types';

  const props = defineProps({
    pagination: { type: Object as PropType<Pagination>, required: true },
  });

  const emit = defineEmits([
    'redirect',
  ]);

  const http = useHttpClient();
  const translator = useTranslator();

  function onClick(url: string | null, page: number) {
    let pageToRedirect = page;

    // if === 0 that means user clicked prev button, if === props.lastPage + 1 that means user clicked next button
    if (page === 0) pageToRedirect = props.pagination.current_page - 1;
    if (page === props.pagination.last_page + 1) pageToRedirect = props.pagination.current_page + 1;

    emit('redirect', pageToRedirect);

    if (url) {
      http.get(url);
    }
  }

  function getAriaLabel(label: string) {
    const numeric = Number(label);
    if (isNaN(numeric)) {
      if (label === '&lsaquo;') return translator.trans('SHOP.COMMON.ARIA.GO_TO_PREVIOUS_PAGE');
      else if (label === '&rsaquo;') return translator.trans('SHOP.COMMON.ARIA.GO_TO_NEXT_PAGE');
    } else {
      return translator.trans('SHOP.COMMON.ARIA.GO_TO_PAGE', { label });
    }
  }
</script>
<template>
  <nav v-if="pagination.last_page > 1" aria-label="Paginacja" class="pagination" role="navigation">
    <ul class="pagination__elements">
      <li v-for="(link, index) in pagination.links" :class="{'primary' : link.active}" class="pagination__item">
        <SfButton
          :aria-current="link.active"
          :aria-label="getAriaLabel(link.label)"
          :disabled="!link.url"
          :primary="link.active"
          rounded
          square
          @click="onClick(link.url, index)"
        >
          <span v-if="index > 0 && index < pagination.links.length - 1" class="pagination__label" v-html="link.label"></span>
          <SfIconChevronLeft v-else-if="index === 0" aria-hidden="true" class="pagination__label" />
          <SfIconChevronRight v-else-if="index === pagination.links.length - 1" aria-hidden="true" class="pagination__label" />
        </SfButton>
      </li>
    </ul>
  </nav>
</template>
<style lang="scss">
  @use "@storefront/styles/mixins/media";

  :root {
    --sf-pagination-gap: 0.75rem;
    --sf-pagination-margin-y: 1.875rem;
    --sf-pagination-margin-x: 0;
  }

  .pagination {
    &__elements {
      display: flex;
      align-items: center;
      justify-content: center;
      list-style: none;
      gap: var(--sf-pagination-gap);
      margin: var(--sf-pagination-margin-y) var(--sf-pagination-margin-x);
    }

    &__label {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 1rem;
      height: 1rem;
    }

    @include media.query(md) {
      &__item {
        display: none;

        &:first-child, &.primary, &:last-child, &:nth-last-child(2), &:nth-child(2) {
          display: flex;
        }
      }
    }
  }
</style>
